/**
 * Audit Detail Page
 * Sprint 14: Full Audit Module
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ArrowLeft,
  ClipboardCheck,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MinusCircle,
  Clock,
  Lock,
  Upload,
  Download,
  Plus,
  Save,
  ChevronDown,
  ChevronRight,
  Link as LinkIcon,
  Calendar,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const RESULT_OPTIONS = [
  { value: 'COMPLIANT', label: 'Compliant', icon: CheckCircle, color: 'text-green-600 bg-green-50' },
  { value: 'PARTIAL', label: 'Partial', icon: MinusCircle, color: 'text-yellow-600 bg-yellow-50' },
  { value: 'NON_COMPLIANT', label: 'Non-Compliant', icon: XCircle, color: 'text-red-600 bg-red-50' },
  { value: 'NA', label: 'N/A', icon: MinusCircle, color: 'text-slate-400 bg-slate-50' },
];

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-700 border-blue-200',
};

const CAPA_STATUS_COLORS = {
  OPEN: 'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  VERIFIED: 'bg-blue-100 text-blue-700',
  CLOSED: 'bg-green-100 text-green-700',
};

const AuditDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [audit, setAudit] = useState(null);
  const [findings, setFindings] = useState([]);
  const [capas, setCAPAs] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('checklist');
  const [expandedSections, setExpandedSections] = useState(['A', 'B', 'C']);
  
  // Finding edit state
  const [editingFinding, setEditingFinding] = useState(null);
  const [findingNotes, setFindingNotes] = useState('');
  const [findingResult, setFindingResult] = useState('');
  const [saving, setSaving] = useState(false);
  
  // CAPA dialog
  const [showCAPADialog, setShowCAPADialog] = useState(false);
  const [capaFinding, setCAPAFinding] = useState(null);
  const [capaForm, setCAPAForm] = useState({ issue_summary: '', corrective_action: '' });
  
  // Evidence upload
  const [uploading, setUploading] = useState(false);
  
  // Confirm dialogs
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showLockDialog, setShowLockDialog] = useState(false);

  const canEdit = user?.role === 'Admin' || user?.role === 'ComplianceManager';
  const canLock = user?.role === 'Admin' || user?.role === 'ComplianceManager';
  const isLocked = audit?.status === 'LOCKED';

  const fetchAudit = useCallback(async () => {
    try {
      const response = await api.get(`/audits/${id}`);
      setAudit(response.data);
    } catch (error) {
      console.error('Error fetching audit:', error);
      toast.error('Failed to load audit');
    }
  }, [id]);

  const fetchFindings = useCallback(async () => {
    try {
      const response = await api.get(`/audits/${id}/findings`);
      setFindings(response.data.findings || []);
    } catch (error) {
      console.error('Error fetching findings:', error);
    }
  }, [id]);

  const fetchCAPAs = useCallback(async () => {
    try {
      const response = await api.get(`/audits/${id}/capas`);
      setCAPAs(response.data.capas || []);
    } catch (error) {
      console.error('Error fetching CAPAs:', error);
    }
  }, [id]);

  const fetchEvidence = useCallback(async () => {
    try {
      const response = await api.get(`/audits/${id}/evidence`);
      setEvidence(response.data.evidence || []);
    } catch (error) {
      console.error('Error fetching evidence:', error);
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAudit(), fetchFindings(), fetchCAPAs(), fetchEvidence()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAudit, fetchFindings, fetchCAPAs, fetchEvidence]);

  const handleEditFinding = (finding) => {
    setEditingFinding(finding);
    setFindingNotes(finding.auditor_notes || '');
    setFindingResult(finding.result || 'NA');
  };

  const handleSaveFinding = async () => {
    if (!editingFinding) return;
    
    try {
      setSaving(true);
      await api.put(`/audits/${id}/findings/${editingFinding.id}`, {
        result: findingResult,
        auditor_notes: findingNotes,
      });
      toast.success('Finding saved');
      setEditingFinding(null);
      await fetchFindings();
      await fetchAudit(); // Refresh summary
    } catch (error) {
      console.error('Error saving finding:', error);
      toast.error('Failed to save finding');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCAPADialog = (finding) => {
    setCAPAFinding(finding);
    setCAPAForm({
      issue_summary: `Non-compliance with ${finding.standard_code}: ${finding.standard?.title || ''}`,
      corrective_action: '',
    });
    setShowCAPADialog(true);
  };

  const handleCreateCAPA = async () => {
    if (!capaFinding) return;
    
    try {
      await api.post(`/audits/${id}/capas`, {
        audit_id: id,
        finding_id: capaFinding.id,
        standard_code: capaFinding.standard_code,
        severity: capaFinding.standard?.severity || 'MEDIUM',
        issue_summary: capaForm.issue_summary,
        corrective_action: capaForm.corrective_action,
      });
      toast.success('CAPA created');
      setShowCAPADialog(false);
      await fetchCAPAs();
      await fetchAudit();
    } catch (error) {
      console.error('Error creating CAPA:', error);
      toast.error('Failed to create CAPA');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tags', '');
      formData.append('note', '');
      
      await api.post(`/audits/${id}/evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Evidence uploaded');
      await fetchEvidence();
    } catch (error) {
      console.error('Error uploading evidence:', error);
      toast.error('Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const handleCompleteAudit = async () => {
    try {
      await api.post(`/audits/${id}/complete`);
      toast.success('Audit marked as complete');
      setShowCompleteDialog(false);
      await fetchAudit();
    } catch (error) {
      console.error('Error completing audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete audit');
    }
  };

  const handleLockAudit = async () => {
    try {
      await api.post(`/audits/${id}/lock`);
      toast.success('Audit locked');
      setShowLockDialog(false);
      await fetchAudit();
    } catch (error) {
      console.error('Error locking audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to lock audit');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/audits/${id}/export`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_report_${id}.html`;
      a.click();
      toast.success('Report exported');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export report');
    }
  };

  // Group findings by section
  const groupedFindings = findings.reduce((acc, finding) => {
    const section = finding.standard_code?.[0] || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(finding);
    return acc;
  }, {});

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getResultIcon = (result) => {
    const option = RESULT_OPTIONS.find(o => o.value === result);
    if (!option) return null;
    const Icon = option.icon;
    return <Icon className={`h-5 w-5 ${option.color.split(' ')[0]}`} />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Loading audit...</div>
        </div>
      </Layout>
    );
  }

  if (!audit) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Audit not found</p>
          <Button onClick={() => navigate('/audits')} className="mt-4">
            Back to Audits
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-detail">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/audits')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <ClipboardCheck className="h-6 w-6 text-avida-blue" />
                {audit.audit_type.replace('_', ' ')}
              </h1>
              <p className="text-slate-600">
                {audit.subject_client_name || audit.subject_personnel_name || 'Agency-wide'}
                {audit.created_by_name && ` • Created by ${audit.created_by_name}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${
              audit.status === 'DRAFT' ? 'bg-slate-100 text-slate-700' :
              audit.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
              audit.status === 'COMPLETE' ? 'bg-green-100 text-green-700' :
              'bg-purple-100 text-purple-700'
            }`}>
              {audit.status === 'LOCKED' && <Lock className="h-3 w-3 mr-1" />}
              {audit.status}
            </Badge>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            {canEdit && !isLocked && audit.status !== 'COMPLETE' && (
              <Button onClick={() => setShowCompleteDialog(true)}>
                Complete Audit
              </Button>
            )}
            {canLock && audit.status === 'COMPLETE' && (
              <Button variant="destructive" onClick={() => setShowLockDialog(true)}>
                <Lock className="h-4 w-4 mr-2" />
                Lock Audit
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {audit.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{audit.summary.compliant}</div>
                <div className="text-sm text-slate-500">Compliant</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{audit.summary.partial}</div>
                <div className="text-sm text-slate-500">Partial</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{audit.summary.non_compliant}</div>
                <div className="text-sm text-slate-500">Non-Compliant</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-slate-400">{audit.summary.na}</div>
                <div className="text-sm text-slate-500">N/A</div>
              </CardContent>
            </Card>
            <Card className={audit.summary.open_capas > 0 ? 'border-red-200' : ''}>
              <CardContent className="p-4 text-center">
                <div className={`text-2xl font-bold ${audit.summary.open_capas > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                  {audit.summary.open_capas}
                </div>
                <div className="text-sm text-slate-500">Open CAPAs</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="checklist" data-testid="tab-checklist">
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Checklist ({findings.length})
            </TabsTrigger>
            <TabsTrigger value="evidence" data-testid="tab-evidence">
              <FileText className="h-4 w-4 mr-2" />
              Evidence ({evidence.length})
            </TabsTrigger>
            <TabsTrigger value="capas" data-testid="tab-capas">
              <AlertTriangle className="h-4 w-4 mr-2" />
              CAPAs ({capas.length})
            </TabsTrigger>
          </TabsList>

          {/* Checklist Tab */}
          <TabsContent value="checklist" className="space-y-4">
            {Object.entries(groupedFindings).sort().map(([section, sectionFindings]) => (
              <Card key={section}>
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => toggleSection(section)}
                >
                  <CardTitle className="flex items-center justify-between">
                    <span>
                      Section {section}: {
                        section === 'A' ? 'Client File Audit' :
                        section === 'B' ? 'Employee File Audit' :
                        section === 'C' ? 'Agency Level Audit' : 'Other'
                      }
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-normal text-slate-500">
                        {sectionFindings.filter(f => f.result === 'COMPLIANT').length}/{sectionFindings.length} compliant
                      </span>
                      {expandedSections.includes(section) ? 
                        <ChevronDown className="h-5 w-5" /> : 
                        <ChevronRight className="h-5 w-5" />
                      }
                    </div>
                  </CardTitle>
                </CardHeader>
                {expandedSections.includes(section) && (
                  <CardContent className="space-y-3">
                    {sectionFindings.map(finding => (
                      <div 
                        key={finding.id}
                        className={`p-4 border rounded-lg ${
                          finding.result === 'NON_COMPLIANT' ? 'border-red-200 bg-red-50/50' :
                          finding.result === 'PARTIAL' ? 'border-yellow-200 bg-yellow-50/50' :
                          finding.result === 'COMPLIANT' ? 'border-green-200 bg-green-50/50' :
                          'border-slate-200'
                        }`}
                        data-testid={`finding-${finding.standard_code}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className={SEVERITY_COLORS[finding.standard?.severity] || ''}>
                                {finding.standard_code}
                              </Badge>
                              <span className="font-medium">{finding.standard?.title}</span>
                              {getResultIcon(finding.result)}
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              {finding.standard?.what_to_check}
                            </p>
                            {finding.standard?.cross_checks && (
                              <p className="text-xs text-slate-500 mb-1">
                                <strong>Cross-checks:</strong> {finding.standard.cross_checks}
                              </p>
                            )}
                            {finding.auditor_notes && (
                              <p className="text-sm text-slate-700 mt-2 p-2 bg-white rounded border">
                                <strong>Notes:</strong> {finding.auditor_notes}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {canEdit && !isLocked && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditFinding(finding)}
                                >
                                  Edit
                                </Button>
                                {(finding.result === 'PARTIAL' || finding.result === 'NON_COMPLIANT') && (
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => handleOpenCAPADialog(finding)}
                                  >
                                    + CAPA
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            ))}
          </TabsContent>

          {/* Evidence Tab */}
          <TabsContent value="evidence">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Evidence Files</CardTitle>
                  {canEdit && !isLocked && (
                    <div>
                      <input
                        type="file"
                        id="evidence-upload"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                      <Button asChild disabled={uploading}>
                        <label htmlFor="evidence-upload" className="cursor-pointer">
                          <Upload className="h-4 w-4 mr-2" />
                          {uploading ? 'Uploading...' : 'Upload Evidence'}
                        </label>
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {evidence.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    No evidence files uploaded yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {evidence.map(file => (
                      <div 
                        key={file.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-slate-400" />
                          <div>
                            <div className="font-medium">{file.file_name}</div>
                            <div className="text-sm text-slate-500">
                              {new Date(file.uploaded_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${file.file_url}`} download>
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CAPAs Tab */}
          <TabsContent value="capas">
            <Card>
              <CardHeader>
                <CardTitle>Corrective Actions (CAPAs)</CardTitle>
                <CardDescription>
                  Required for PARTIAL or NON_COMPLIANT findings before audit can be completed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {capas.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    No CAPAs created yet. Add CAPAs from non-compliant findings in the Checklist tab.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {capas.map(capa => (
                      <div 
                        key={capa.id}
                        className="p-4 border rounded-lg"
                        data-testid={`capa-${capa.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className={SEVERITY_COLORS[capa.severity]}>
                                {capa.standard_code}
                              </Badge>
                              <Badge className={CAPA_STATUS_COLORS[capa.status]}>
                                {capa.status}
                              </Badge>
                            </div>
                            <p className="font-medium mb-1">{capa.issue_summary}</p>
                            {capa.corrective_action && (
                              <p className="text-sm text-slate-600">
                                <strong>Action:</strong> {capa.corrective_action}
                              </p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Due: {new Date(capa.due_date).toLocaleDateString()}
                              </span>
                              {capa.owner_name && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {capa.owner_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Finding Dialog */}
        <Dialog open={!!editingFinding} onOpenChange={() => setEditingFinding(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Edit Finding: {editingFinding?.standard_code}
              </DialogTitle>
              <DialogDescription>
                {editingFinding?.standard?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Result</Label>
                <Select value={findingResult} onValueChange={setFindingResult}>
                  <SelectTrigger data-testid="finding-result-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESULT_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Auditor Notes</Label>
                <Textarea
                  value={findingNotes}
                  onChange={(e) => setFindingNotes(e.target.value)}
                  placeholder="Add your findings, observations, and evidence notes..."
                  rows={4}
                  data-testid="finding-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingFinding(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveFinding} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Finding'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create CAPA Dialog */}
        <Dialog open={showCAPADialog} onOpenChange={setShowCAPADialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create CAPA</DialogTitle>
              <DialogDescription>
                Corrective Action for {capaFinding?.standard_code}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Issue Summary</Label>
                <Textarea
                  value={capaForm.issue_summary}
                  onChange={(e) => setCAPAForm(f => ({ ...f, issue_summary: e.target.value }))}
                  rows={3}
                  data-testid="capa-issue"
                />
              </div>
              <div className="space-y-2">
                <Label>Corrective Action Plan</Label>
                <Textarea
                  value={capaForm.corrective_action}
                  onChange={(e) => setCAPAForm(f => ({ ...f, corrective_action: e.target.value }))}
                  placeholder="Describe the corrective action to be taken..."
                  rows={3}
                  data-testid="capa-action"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCAPADialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCAPA}>
                Create CAPA
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Audit Confirmation */}
        <AlertDialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Complete Audit?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the audit as complete. Ensure all PARTIAL and NON_COMPLIANT 
                findings have CAPAs assigned before completing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleCompleteAudit}>
                Complete Audit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Lock Audit Confirmation */}
        <AlertDialog open={showLockDialog} onOpenChange={setShowLockDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Lock Audit?</AlertDialogTitle>
              <AlertDialogDescription>
                Locking the audit will prevent any further changes. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleLockAudit} className="bg-red-600 hover:bg-red-700">
                Lock Audit
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AuditDetail;
